import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
  type ReactElement
} from 'react'
import type { ThemeMode, ResolvedTheme } from '../styles/theme'

// ============================================================================
// Types
// ============================================================================

/** Theme context value */
interface ThemeContextValue {
  /** Current theme mode setting */
  themeMode: ThemeMode
  /** Resolved theme (what is actually applied) */
  resolvedTheme: ResolvedTheme
  /** Set the theme mode */
  setThemeMode: (mode: ThemeMode) => void
  /** Toggle between light and dark (ignores system/high-contrast) */
  toggleTheme: () => void
  /** Whether the resolved theme is dark */
  isDark: boolean
  /** Whether high contrast mode is enabled */
  isHighContrast: boolean
}

/** Props for ThemeProvider */
interface ThemeProviderProps {
  children: ReactNode
  /** Default theme mode if no preference is stored */
  defaultTheme?: ThemeMode
  /** Storage key for persisting theme preference */
  storageKey?: string
}

// ============================================================================
// Constants
// ============================================================================

const THEME_STORAGE_KEY = 'petru-theme-mode'
const TRANSITION_DURATION = 200

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null)

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Resolve a theme mode to an actual theme
 */
function resolveThemeMode(mode: ThemeMode): ResolvedTheme {
  return mode
}

/**
 * Get initial theme mode from localStorage or default
 */
function getInitialThemeMode(storageKey: string, defaultTheme: ThemeMode): ThemeMode {
  if (typeof window === 'undefined') return defaultTheme

  const stored = localStorage.getItem(storageKey)
  if (stored === 'light' || stored === 'dark' || stored === 'high-contrast') {
    return stored
  }

  return defaultTheme
}

/**
 * Apply theme classes to the document element
 */
function applyThemeToDocument(resolvedTheme: ResolvedTheme, enableTransition: boolean): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  // Add transition class for smooth color changes
  if (enableTransition) {
    root.classList.add('theme-transition')
  }

  // Remove all theme classes
  root.classList.remove('light', 'dark', 'high-contrast')

  // Add the resolved theme class
  root.classList.add(resolvedTheme)

  // Update meta theme-color for browser chrome and PWA status bar
  const metaThemeColor = document.getElementById('theme-color-meta') || document.querySelector('meta[name="theme-color"]')
  if (metaThemeColor) {
    const colors: Record<ResolvedTheme, string> = {
      light: '#f9fafb',
      dark: '#111827',
      'high-contrast': '#ffffff'
    }
    metaThemeColor.setAttribute('content', colors[resolvedTheme])
  }

  // Remove transition class after animation completes
  if (enableTransition) {
    setTimeout(() => {
      root.classList.remove('theme-transition')
    }, TRANSITION_DURATION)
  }
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Theme provider for dark mode and high contrast support
 *
 * Features:
 * - Light, dark, and high-contrast modes
 * - Persists preference to localStorage
 * - Smooth color transitions
 * - WCAG AA compliant colors
 */
export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = THEME_STORAGE_KEY
}: ThemeProviderProps): ReactElement {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() =>
    getInitialThemeMode(storageKey, defaultTheme)
  )
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveThemeMode(getInitialThemeMode(storageKey, defaultTheme))
  )
  const [isInitialized, setIsInitialized] = useState(false)

  // Apply theme to document whenever resolved theme changes
  useEffect(() => {
    const newResolved = resolveThemeMode(themeMode)
    setResolvedTheme(newResolved)
    applyThemeToDocument(newResolved, isInitialized)

    // Persist to localStorage
    localStorage.setItem(storageKey, themeMode)

    // Mark as initialized after first render
    if (!isInitialized) {
      setIsInitialized(true)
    }
  }, [themeMode, storageKey, isInitialized])

  // Set theme mode with validation
  const setThemeMode = useCallback((mode: ThemeMode): void => {
    if (mode === 'light' || mode === 'dark' || mode === 'high-contrast') {
      setThemeModeState(mode)
    }
  }, [])

  // Toggle between light and dark (simple toggle)
  const toggleTheme = useCallback((): void => {
    setThemeModeState(current => {
      // If currently on high-contrast, switch to light
      if (current === 'high-contrast') {
        return 'light'
      }
      return current === 'dark' ? 'light' : 'dark'
    })
  }, [])

  // Derived state
  const isDark = resolvedTheme === 'dark'
  const isHighContrast = resolvedTheme === 'high-contrast'

  // Memoized context value
  const value = useMemo<ThemeContextValue>(() => ({
    themeMode,
    resolvedTheme,
    setThemeMode,
    toggleTheme,
    isDark,
    isHighContrast
  }), [themeMode, resolvedTheme, setThemeMode, toggleTheme, isDark, isHighContrast])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access theme context
 * @throws Error if used outside ThemeProvider
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// ============================================================================
// Exports
// ============================================================================

export default ThemeContext
export type { ThemeContextValue, ThemeProviderProps }
